import { useQuery, useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { QueryKeys } from '@/types/query';
import * as ItemService from '@/services/item';
import { ServiceError, ClientItemPath, ServerErrorTwo } from '@/types/service';
import { Result } from '@/types/common';
import {
  ActionLogType,
  ClientItems,
  Item,
  ItemAttrPath,
  ItemReturnReasonFilter,
  ShipmentDirection,
  TimelineType,
  TrackingComment,
} from '@/types/item';
import { toRQMutation, toRQQuery } from '@/utilities/service';
import { mergeClientItems } from '@/utilities/client';
import { AxiosError, AxiosResponse } from 'axios';

export function useItem(id: number, enabled?: boolean) {
  const { remove, isFetching, isRefetching, data, refetch } = useQuery({
    queryKey: [QueryKeys.ITEM, id],
    queryFn: () => ItemService.getItem(id),
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    enabled: enabled != null ? enabled : id > 0,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useHisto(id: number) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.HISTO, id],
    queryFn: () => ItemService.getHisto(id),
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useOmsReturnStatus(id: number) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.OMS_RETURN_STATUS, id],
    queryFn: () => ItemService.getOmsReturnStatus(id),
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useClientItems(id: number, path: ClientItemPath, perPage: number) {
  // TODO: If you need the second useInfiniteQuery please write a toRQInfiniteQuery
  const { isFetching, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [QueryKeys.ITEM, id, path, perPage],
    queryFn: ({ pageParam = 1 }) => ItemService.getClientItems(id, path, Number(pageParam), perPage),
    getNextPageParam: (_, pages) => pages.length + 1,
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  });

  const hasData = data && data.pages[0].kind === 'Ok' && data.pages[0].data.products[0] != null;
  const isLoading = isFetching && !hasData;
  const isLoadingMore = isFetching && hasData;
  const isEmpty = !isLoading && !hasData;
  const returnData: Result<ServiceError, ClientItems> = data
    ? { kind: 'Ok', data: mergeClientItems(data.pages) }
    : { kind: 'Ko', error: { kind: 'QueryError' } };

  return { data: returnData, isLoading, isLoadingMore, isEmpty, fetchNextPage };
}

export function useItemSizes(attrPath: ItemAttrPath, productId: number, sectionId?: number) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.ITEM_ATTRIBUTES, attrPath, productId, sectionId],
    queryFn: () => ItemService.getItemSizes(attrPath, productId, sectionId),
    staleTime: Infinity,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useItemAttributes(attrPath: ItemAttrPath, productId: number, includeId: number, sectionId?: number) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.ITEM_ATTRIBUTES, attrPath, productId, includeId, sectionId],
    queryFn: () => ItemService.getItemAttributes(attrPath, productId, includeId, sectionId),
    staleTime: Infinity,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useItemSections() {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.ITEM_SECTIONS],
    queryFn: () => ItemService.getItemSections(),
    staleTime: Infinity,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useItemBrands(brandId?: number) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.ITEM_BRANDS, brandId],
    queryFn: () => ItemService.getItemBrands(brandId),
    staleTime: Infinity,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useItemModels(brandId: number, sectionId: number) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.ITEM_MODELS, brandId, sectionId],
    queryFn: () => ItemService.getItemModels(brandId, sectionId),
    staleTime: Infinity,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useUpdateItemAttribute(productId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (data: ItemService.UpdateItemAttributeData) => ItemService.updateItemAttribute(productId, data),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useUpdateItemDescription(productId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (data: ItemService.UpdateItemDescriptionData) => ItemService.updateItemDescription(productId, data),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useUpdateItem(productId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (data: Partial<Item>) => ItemService.updateItem(productId, data),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useCancelOrder(productId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: ({ reasonId, comment }: { reasonId: string; comment?: string }) =>
      ItemService.cancelOrder(productId, reasonId, comment),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useItemReturnReasons(filter: ItemReturnReasonFilter) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.ITEM_RETURN_REASONS, filter],
    queryFn: () => ItemService.getItemReturnReasons(filter),
    staleTime: Infinity,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useItemWarehousePicking(productId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (data: ItemService.ItemWarehousePickingData) => ItemService.itemWarehousePicking(productId, data),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useItemRelistSeller(productId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (data: ItemService.ItemRelistSellerData) => ItemService.itemRelistSeller(productId, data),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useItemRelistPrice(productId: number) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.ITEM_RELIST_PRICE, productId],
    queryFn: () => ItemService.getItemRelistPrice(productId),
    staleTime: Infinity,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useItemRelistBuyer(productId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (data: ItemService.ItemRelistBuyerData) => ItemService.itemRelistBuyer(productId, data),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useItemReleasePayment(productId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: () => ItemService.itemReleasePayment(productId),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useItemCancelEarlyPayout(productId: number) {
  return useMutation<AxiosResponse, AxiosError<ServerErrorTwo>>({
    mutationFn: () => ItemService.itemCancelEarlyPayout(productId),
  });
}

export function useItemSendToWarehouse(productId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: () => ItemService.itemSendToWarehouse(productId),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useItemSendToSeller(productId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: () => ItemService.itemSendToSeller(productId),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useItemCurrencies() {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.ITEM_CURRENCIES],
    queryFn: () => ItemService.getItemCurrencies(),
    staleTime: Infinity,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useItemLostInTransit(productId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: () => ItemService.itemLostInTransit(productId),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useItemWithdrawFromSale(productId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: () => ItemService.itemWithdrawFromSale(productId),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useItemCurationPriority(productId: number) {
  return useMutation<AxiosResponse, AxiosError<ServerErrorTwo>>({
    mutationFn: () => ItemService.itemCurationPriority(productId),
  });
}

export function useItemMmaos(id: number, page: number, pageSize: number, enabled: boolean) {
  return useQuery({
    queryKey: [QueryKeys.ITEM_MMAO, id, page, pageSize],
    queryFn: () => ItemService.getItemMmaos(id, page, pageSize),
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    enabled,
  });
}

export function useItemPhotos(id: number, enabled: boolean) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.ITEM_PHOTOS, id],
    queryFn: () => ItemService.getItemPhotos(id),
    staleTime: Infinity,
    enabled,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useUploadItemPhoto(productId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (photo: File) => ItemService.uploadItemPhoto(productId, photo),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useDeleteItemPhoto(productId: number, photoName: string) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: () => ItemService.deleteItemPhoto(productId, photoName),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useReorderItemPhotos(productId: number, photoList: string[]) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: () => ItemService.reorderItemPhotos(productId, photoList),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useUpdateItemOrderCancellation(productId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (data: ItemService.UpdateItemOrderCancellationData) =>
      ItemService.updateItemOrderCancellation(productId, data),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useUpdateItemInterception(productId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (data: ItemService.UpdateItemInterceptionData) => ItemService.updateItemInterception(productId, data),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useCreateItemReturnLitigation() {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (param: ItemService.CreateReturnLitigation) => ItemService.createItemReturnLitigation(param),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useGetItemCreateReturnLitigation({
  productId,
  litigation,
  clientType,
  isReplacementTicket = false,
}: {
  productId: number;
  litigation: string;
  clientType?: TimelineType | '';
  isReplacementTicket?: boolean;
}) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.ITEM_CREATE_RETURN_LITIGATION, productId, litigation, clientType, isReplacementTicket],
    queryFn: () =>
      ItemService.getItemCreateReturnLitigation({
        id: productId,
        isReplacementTicket,
        litigation,
        clientType,
      }),
    // there're created tickets with the same id, so we need to disable cache
    cacheTime: 0,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useGetItemReplaceReturnLitigation(id: number) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.ITEM_CREATE_RETURN_LITIGATION, id],
    queryFn: () => ItemService.getItemCreateReturnLitigation({ id, isReplacementTicket: true }),
    // there're created tickets with the same id, so we need to disable cache
    cacheTime: 0,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useSubmitShippingLabel(id: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (param: ItemService.SubmitShippingLabel) => ItemService.submitShippingLabel(id, param),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useGetItemReturnFormLink(id: number) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.ITEM_RETURN_FORM_LINK, id],
    queryFn: () => ItemService.getItemReturnFormLink(id),
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useItemRequireAuthentication(id: number, enabled: boolean) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.ITEM_MMAO, id],
    queryFn: () => ItemService.getItemRequireAuthentication(id),
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    enabled,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useActionTracking(productId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (comment: TrackingComment) => ItemService.trackAction(productId, comment),
  });
  return toRQMutation(data, isLoading, mutate);
}
export function useClientActionTracking(clientId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (comment: TrackingComment) => ItemService.trackClientAction(clientId, comment),
  });
  return toRQMutation(data, isLoading, mutate);
}

export function useActionLogs({
  type,
  id,
  page,
  pageSize,
  enabled,
}: {
  type: ActionLogType;
  id: number;
  page: number;
  pageSize: number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [QueryKeys.ITEM_ACTION_LOG, type, id, page, pageSize],
    queryFn: () => ItemService.getActionLogs(type, id, page, pageSize),
    enabled,
  });
}

export function useCancelReasons() {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.CANCEL_REASONS],
    queryFn: () => ItemService.getCancelReasons(),
    staleTime: Infinity,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useGenerateShippingLabelUrl(productId: number) {
  return useMutation<ItemService.RequestNonSponsoredShippingLabelResponse, AxiosError<ServerErrorTwo>>({
    mutationFn: () => ItemService.requestNonSponsoredShippingLabel(productId),
  });
}

export function useCreateShippingLabel(productId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (data: ItemService.CreateShippingLabelData) => ItemService.createShippingLabel(productId, data),
  });

  return toRQMutation(data, isLoading, mutate);
}

/**
 * @todo replace this with a proper `useQuery` + query invalidation
 */
export function useGetShippingConvenienceLabel(productId: number, direction: ShipmentDirection) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: () => ItemService.getShippingConvenienceLabel(productId, direction),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useGetCarriers(originCountry: string) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.CARRIERS, originCountry],
    queryFn: () => ItemService.getCarriers(originCountry),
    staleTime: Infinity,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function usePostalLitigation({ productId, enabled }: { productId: string; enabled?: boolean }) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.POSTAL_LITIGATION, productId],
    queryFn: () => ItemService.getPostalLitigation(productId),
    enabled,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useConfirmLitigation() {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (payload: { productId: string; moment: string; time: string }) =>
      ItemService.confirmLitigation(payload),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useConfirmDelivered({
  tmsShipmentId,
  buyerId,
  productId,
}: {
  tmsShipmentId: number;
  buyerId: number;
  productId: number;
}) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: () => ItemService.confirmDelivered({ tmsShipmentId, buyerId, productId }),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useGetPLDecisions(productId: number) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.POSTAL_LITIGATION_DECISIONS, productId],
    queryFn: () => ItemService.getPLDecisions(productId),
    enabled: !!productId,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useSubmitPLDecision() {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (payload: { productId: number; action: string; type: string; decisionComment: string }) =>
      ItemService.submitPLDecision(payload),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function usePayByLinkDetail(productId: number) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.PAY_BY_LINK_DETAIL, productId],
    queryFn: () => ItemService.getPayByLinkDetail(productId),
    // no cache
    cacheTime: 0,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useGetPayByLinkForm(productId: number) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.PAY_BY_LINK_FORM, productId],
    queryFn: () => ItemService.getPayByLinkForm(productId),
    // no cache
    cacheTime: 0,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useSubmitPayByLink(productId: number) {
  const { isLoading, data, mutate } = useMutation({
    mutationFn: (data: ItemService.PayByLinkSubmitPayload) => ItemService.submitPayByLink(productId, data),
  });

  return toRQMutation(data, isLoading, mutate);
}

export function useDeletePayByLink(productId: number) {
  const mutation = useMutation({
    mutationFn: () => ItemService.deletePayByLink(productId),
  });

  return toRQMutation(mutation.data, mutation.isLoading, mutation.mutate);
}

export function usePayoutStatusDetail(payoutId: number) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.PAYOUT_STATUS_DETAIL, payoutId],
    queryFn: () => ItemService.getPayoutStatusDetail(payoutId),
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useGenerateInvoiceRequestUrl(productId: number, clientId: number) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.COMPLIANCE_INVOICE_REQUEST, productId, clientId],
    queryFn: () => ItemService.generateInvoiceRequestUrl(productId, clientId),
    enabled: false,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useGetShippingReminder(productId: number) {
  return useQuery({
    queryKey: [QueryKeys.GET_SHIPPING_REMINDER, productId],
    queryFn: () => ItemService.getShippingReminder(productId),
    enabled: !!productId,
  });
}

export function usePurchasePlaces() {
  return useQuery({
    queryKey: [QueryKeys.GET_PURCHASE_PLACES],
    queryFn: () => ItemService.getPurchasePlaces(),
    staleTime: Infinity,
  });
}

export function useGetAuthenticationAskInfo(productId: number) {
  return useQuery({
    queryKey: [QueryKeys.GET_AUTHENTICATION_ASK_INFO, productId],
    queryFn: () => ItemService.getAuthenticationAskInfo(productId),
  });
}

export function useSubmitAuthenticationAskInfo(productId: number) {
  return useMutation<unknown, AxiosError<ServerErrorTwo>, ItemService.SubmitAuthenticationAskInfoPayload>({
    mutationKey: [QueryKeys.SUBMIT_AUTHENTICATION_ASK_INFO, productId],
    mutationFn: (data) => ItemService.submitAuthenticationAskInfo(productId, data),
  });
}

export function useGetSellerShippingStatus(productId: number, types?: string[]) {
  return useQuery({
    queryKey: [QueryKeys.GET_SELLER_SHIPPING_STATUS, productId, types],
    queryFn: () => ItemService.getSellerShippingStatus(productId, types),
    enabled: false,
  });
}

export function useRestartOmsWorkflow() {
  return useMutation<unknown, AxiosError<ServerErrorTwo>, { productId: number }>({
    mutationFn: ({ productId }) => ItemService.restartOmsWorkflow(productId),
  });
}
